import { styled, TableContainer, Box, Pagination } from '@mui/material';
import { Theme } from '@mui/material/styles';

import { colors } from '../theme/theme';

// Shared border-radius treatment applied to the first/last cell of a row across multiple selectors
const getEdgeCellRadiusStyles = (theme: Theme) => ({
    '&:first-of-type': {
        borderTopLeftRadius: theme.spacing(1),
        borderBottomLeftRadius: theme.spacing(1),
    },
    '&:last-of-type': {
        borderTopRightRadius: theme.spacing(1),
        borderBottomRightRadius: theme.spacing(1),
    },
});

// Shared tooltip text-ellipsis treatment, parameterised by max width
const getTooltipEllipsisStyles = (maxWidth: number) => ({
    maxWidth,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
});

export const TableContainerWrapper = styled(TableContainer)(({ theme }) => ({
    padding: theme.spacing(2),
    backgroundColor: colors.fullWhite,
    borderRadius: theme.spacing(1.5),
    '& .MuiTable-root': {
        '& .MuiTableHead-root': {
            '& .MuiTableCell-root': {
                ...theme.typography.body2,
                color: colors.fullBlack,
                fontWeight: 600,
                padding: theme.spacing(1.5, 2),
                lineHeight: '17px',
                border: 'none',
                backgroundColor: colors.greyPreset[90],
                ...getEdgeCellRadiusStyles(theme),
            },
        },
        '& .MuiTableBody-root': {
            '& .MuiTableRow-root': {
                '& .MuiTableCell-root': {
                    ...theme.typography.body2,
                    color: theme.palette.text.secondary,
                    fontWeight: 400,
                    padding: theme.spacing(1.25, 2),
                    lineHeight: '21px',
                    borderBottom: `1px solid ${colors.greyPreset[30]}`,
                    wordWrap: 'break-word',
                    '& .tooltip-text-ellipsis': getTooltipEllipsisStyles(200),
                    '& .tooltip-text-ellipsis-wide': getTooltipEllipsisStyles(400),
                },
                '&.MuiTableRow-root:last-child': {
                    '& .MuiTableCell-root': {
                        borderBottom: 'none',
                    },
                },
            },
        },
    },
    '&.tableWrapper': {
        boxShadow: 'none',
        border: `1px solid ${colors.greyPreset[20]}`,
        '& .MuiTable-root': {
            '& .MuiTableCell-root': {
                padding: theme.spacing(2.25, 2),
                lineHeight: '18px',
            },
            '& .MuiTableBody-root': {
                '& .MuiTableRow-root:last-child': {
                    backgroundColor: colors.greyPreset[90],
                    borderRadius: theme.spacing(1),
                    '& .MuiTableCell-root': {
                        borderBottom: 'none',
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                    },
                },
                '& .MuiTableCell-root:last-child': {
                    fontWeight: 600,
                },
                '& .MuiTableRow-root:nth-last-of-type(2)': {
                    '& .MuiTableCell-root': {
                        borderBottom: 'none',
                    },
                },
                '& .MuiTableCell-root': getEdgeCellRadiusStyles(theme),
            },
        },
    },
    '&.tableWrapperLG': {
        '& .MuiTable-root': {
            '& .MuiTableHead-root': {
                '& .MuiTableCell-root': {
                    padding: theme.spacing(2.3, 2),
                },
            },
            '& .MuiTableBody-root': {
                '& .MuiTableRow-root': {
                    '& .MuiTableCell-root': {
                        padding: '20px 16px 16px 16px',
                    },
                },
            },
        },
    },
    '&.accordionTableWrapper': {
        '& .MuiTable-root': {
            '& .MuiTableBody-root': {
                '& .MuiTableRow-root:nth-of-type(odd)': {
                    '& .MuiTableCell-root': {
                        borderBottom: 'none',
                    },
                },
            },
        },
    },
}));

export const PaginationWrapper = styled(Box)(() => ({
    display: 'flex',
    justifyContent: 'end',
    alignItems: 'center',
    padding: 16,
    paddingBottom: 0,
}));

// Shared styling for the previous/next pagination nav items (identical for both)
const navPaginationItemStyles = {
    color: colors.primaryLight[100],
    border: `1px solid ${colors.greyPreset[140]}`,
    backgroundColor: colors.fullWhite,
    '&.Mui-selected': {
        color: colors.fullWhite,
        border: `1px solid ${colors.primaryLight[100]}`,
        backgroundColor: colors.primaryLight[100],
        '&:hover': {
            backgroundColor: colors.primaryLight[100],
            color: colors.fullWhite,
        },
    },
};

export const StyledPagination = styled(Pagination)(() => ({
    '& .MuiPaginationItem-root': {
        borderRadius: '50%',
        minWidth: '32px',
        height: '32px',
        margin: '0 4px',
    },
    '& .MuiPaginationItem-page': {
        color: colors.greyPreset[140],
        border: `1px solid ${colors.greyPreset[140]}`,
        backgroundColor: colors.fullWhite,
        '&:hover': {
            border: `1px solid ${colors.primaryLight[100]}`,
        },
        '&.Mui-selected': {
            color: colors.primaryLight[100],
            border: `1px solid ${colors.primaryLight[100]}`,
            backgroundColor: colors.fullWhite,
            '&:hover': {
                backgroundColor: colors.primaryLight[100],
                color: colors.fullWhite,
            },
        },
    },
    '& .MuiPagination-ul li:first-child .MuiPaginationItem-root, & .MuiPaginationItem-root[aria-label*="previous"], & .MuiPaginationItem-root[aria-label*="Previous"]':
        navPaginationItemStyles,
    '& .MuiPagination-ul li:last-child .MuiPaginationItem-root, & .MuiPaginationItem-root[aria-label*="next"], & .MuiPaginationItem-root[aria-label*="Next"]':
        navPaginationItemStyles,
    '& .MuiPaginationItem-ellipsis': {
        color: colors.greyPreset[140],
        border: `1px solid ${colors.greyPreset[140]}`,
        backgroundColor: colors.fullWhite,
    },
    '& .MuiButtonBase-root': {
        '& svg': {
            fontSize: 24,
        },
    },
}));

export const AccordionTableDetailsWrapper = styled(Box)(() => ({
    marginBottom: 16,
    backgroundColor: colors.greyPreset[90],
    padding: '16px',
    borderTop: `1px solid ${colors.greyPreset[30]}`,
}));
